import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Textarea } from "@/shared/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { Label } from "@/shared/components/ui/label";
import { RiskLevelKey, RiskTypeKey } from "@/services/backend/qpm/types/enums";
import {
  newRequirementAreaRisk,
  newRequirementAreaRisks,
  RequirementArea,
  RequirementAreaRisk,
} from "@/services/backend/qpm/requirements/types/requirement";
import { RiskTypeUtil } from "@/services/backend/qpm/lib/risk-type-util";
import { RiskLevelUtil } from "@/services/backend/qpm/lib/risk-level-util";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import t from "@/lang/lang";
import { IntNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import { useRequirementEdit } from "@/services/backend/qpm/requirements/hooks/requirement-context";

const RISK_TYPE_ORDER = [
  RiskTypeKey.FlyingInsect,
  RiskTypeKey.CrawlingInsect,
  RiskTypeKey.Rodent,
] as const;

const RISK_LEVEL_ORDER = [
  RiskLevelKey.None,
  RiskLevelKey.Low,
  RiskLevelKey.Medium,
  RiskLevelKey.High,
] as const;

const RISK_LEVEL_COLORS: Record<RiskLevelKey, string> = {
  [RiskLevelKey.Unspecified]: "",
  [RiskLevelKey.None]: "",
  [RiskLevelKey.Low]: "text-yellow-300",
  [RiskLevelKey.Medium]: "text-orange-400",
  [RiskLevelKey.High]: "text-red-600",
};

interface RiskAnalysisProps {
  reqArea: RequirementArea;
}

export function RiskAnalysisComponent({ reqArea }: RiskAnalysisProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="h-8" variant="outline">
          {t("Risikobewertung")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-full overflow-scroll overflow-y-scroll sm:max-w-full xl:max-w-1/2">
        <PersistenceContextProvider>
          <DialogHeader>
            <div className="flex items-end justify-between">
              <div>
                <DialogTitle>
                  {t("Risikobewertung")} {reqArea.area.base.name}
                </DialogTitle>
                <DialogDescription>
                  {t("Bitte bewerten Sie das Risiko für den Bereich")}{" "}
                  <span className="font-bold">{reqArea.area.base.name}</span>.
                </DialogDescription>
              </div>
              <PersistenceStateUsingContext />
            </div>
          </DialogHeader>
          <RecommendedMeasuresComponent reqArea={reqArea} />
          {RISK_TYPE_ORDER.map((riskTypeKey) => {
            let riskList = reqArea.risks;
            if (!riskList) riskList = newRequirementAreaRisks(reqArea.areaId);
            let areaRisk = riskList.find(
              (risk) => risk.riskTypeKey === riskTypeKey,
            );
            if (!areaRisk) {
              areaRisk = newRequirementAreaRisk(reqArea.areaId, riskTypeKey);
            }
            return (
              <RiskTypeComponent
                key={riskTypeKey}
                riskType={riskTypeKey}
                areaRiskData={areaRisk}
              />
            );
          })}
        </PersistenceContextProvider>
      </DialogContent>
    </Dialog>
  );
}

function RiskTypeComponent({
  riskType,
  areaRiskData,
}: {
  riskType: RiskTypeKey;
  areaRiskData: RequirementAreaRisk;
}) {
  const { putRequest, updateRequirement } = useRequirementEdit();

  const handleRiskLevelChange = (riskLevelKey: RiskLevelKey) => {
    const updatedRiskData = {
      ...areaRiskData,
      riskLevelKey,
    };

    const updatedAreaList = putRequest.requirement.areaList.map((area) => {
      if (area.areaId === areaRiskData.areaId) {
        let areaRisks = area.risks;
        if (!areaRisks) {
          areaRisks = newRequirementAreaRisks(areaRiskData.areaId);
        }
        return {
          ...area,
          risks: areaRisks.map((risk: RequirementAreaRisk) =>
            risk.riskTypeKey === riskType ? updatedRiskData : risk,
          ),
        };
      }
      return area;
    });

    updateRequirement({ areaList: updatedAreaList });
  };

  const handleTextFieldUpdate = (
    field: keyof Pick<
      RequirementAreaRisk,
      "detection" | "recommendation" | "measure"
    >,
    value: string,
  ) => {
    const updatedRiskData = {
      ...areaRiskData,
      [field]: value,
    };
    const updatedAreaList = putRequest.requirement.areaList.map((area) => {
      if (area.areaId === areaRiskData.areaId) {
        let areaRisks = area.risks;
        if (!areaRisks) {
          areaRisks = newRequirementAreaRisks(areaRiskData.areaId);
        }
        return {
          ...area,
          risks: areaRisks.map((risk: RequirementAreaRisk) =>
            risk.riskTypeKey === riskType ? updatedRiskData : risk,
          ),
        };
      }
      return area;
    });

    updateRequirement({ areaList: updatedAreaList });
  };

  return (
    <>
      <span className="text-xl font-bold">
        {RiskTypeUtil.toLongString(riskType)}
      </span>
      <div className="flex flex-col gap-2">
        <RadioGroup
          className="mb-2 flex gap-x-4 gap-y-2"
          value={areaRiskData.riskLevelKey}
        >
          {RISK_LEVEL_ORDER.map((riskLevelKey) => (
            <div key={riskLevelKey} className="flex items-center space-x-2">
              <RadioGroupItem
                className={RISK_LEVEL_COLORS[riskLevelKey]}
                value={riskLevelKey}
                id={`${riskType}-${riskLevelKey}`}
                onClick={() => handleRiskLevelChange(riskLevelKey)}
              />
              <Label htmlFor={`${riskType}-${riskLevelKey}`}>
                {RiskLevelUtil.toLongString(riskLevelKey)}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <TextFieldsGrid
          riskData={areaRiskData}
          onTextFieldUpdate={handleTextFieldUpdate}
        />
      </div>
    </>
  );
}

function TextFieldsGrid({
  riskData,
  onTextFieldUpdate,
}: {
  riskData: RequirementAreaRisk;
  onTextFieldUpdate: (
    field: keyof Pick<
      RequirementAreaRisk,
      "detection" | "recommendation" | "measure"
    >,
    value: string,
  ) => void;
}) {
  return (
    <div className="grid grid-cols-3 flex-col items-start gap-2">
      <TextFieldWithLabel
        id="textDetection"
        label="Feststellung"
        placeholder="Feststellung..."
        value={riskData.detection}
        onChange={(value) => onTextFieldUpdate("detection", value)}
      />
      <TextFieldWithLabel
        id="textRecommendation"
        label="Empfehlungen"
        placeholder="Empfehlungen..."
        value={riskData.recommendation}
        onChange={(value) => onTextFieldUpdate("recommendation", value)}
      />
      <TextFieldWithLabel
        id="textMeasure"
        label="Maßnahmen"
        placeholder="Maßnahmen..."
        value={riskData.measure}
        onChange={(value) => onTextFieldUpdate("measure", value)}
      />
    </div>
  );
}

function TextFieldWithLabel({
  id,
  label,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export type RecommendedAmountOfMeasuresComponentProps = {
  reqArea: RequirementArea;
};

function RecommendedMeasuresComponent({
  reqArea,
}: RecommendedAmountOfMeasuresComponentProps) {
  const { putRequest, updateRequirement } = useRequirementEdit();

  const handleAmountOfMeasureChange = (amountOfMeasures: number) => {
    const updatedAreaList = putRequest.requirement.areaList.map((area) => {
      if (area.areaId === reqArea.areaId) {
        return {
          ...area,
          amountOfMeasures,
        };
      }
      return area;
    });

    const updatedSelectedMonitors = putRequest.requirement.selectedMonitors.map(
      (monitor: (typeof putRequest.requirement.selectedMonitors)[number]) => {
        if (monitor.areaId === reqArea.areaId) {
          return {
            ...monitor,
            amountOfMeasures,
          };
        }
        return monitor;
      },
    );

    updateRequirement({
      areaList: updatedAreaList,
      selectedMonitors: updatedSelectedMonitors,
    });
  };

  return (
    <div className="flex items-center gap-2">
      <span className="font-medium">
        {t("Empfohlene Anzahl an Maßnahmen im Jahr")}
      </span>
      <IntNumberInput
        value={reqArea.amountOfMeasures}
        onChange={handleAmountOfMeasureChange}
      />
    </div>
  );
}
